import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import classnames from "classnames";
import Form from "../../../../base/form";
import {Fade, SvgIcon} from "@mui/material";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import IconButton from "../../../../base/button/icon_button";
import {DeleteOutlined, Edit, StarBorderOutlined, StarRounded, Visibility, VisibilityOff} from "@mui/icons-material";
import {ReactComponent as ToggleIcon} from "../../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import {deepEqual} from "../../../../../../core/services/utils/utils";
import SaveOrRollback from "../../../save-or-rollback";
import {Col, Collapse} from "reactstrap";
import TryAgain from "../../../try-again";
import Button from "../../../../base/button/button";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";


const reasonTitle = (statusId, reasonId) => `status-${statusId}-reasonTitle-${reasonId}`;
const reasonCode = (statusId, reasonId) => `status-${statusId}-reasonCode-${reasonId}`;

/**
 * Creates the schema and initial values of the given status based on its own information and all of its reasons
 * information.
 * @param {any} status
 * @return {{schema, initialValues: {reasonTitle: string, statusTitle: (*|string), reasonCode: string}}}
 */
const createSchemaAndInitialValues = (status) => {
    const schema = {
        statusTitle: Yup.string().trim().nullable().required(ValidateMessages.required),
        reasonCode: Yup.string().trim().nullable().required(ValidateMessages.required),
        reasonTitle: Yup.string().trim().nullable().required(ValidateMessages.required),
    }
    const initialValues = {
        statusTitle: status?.title ?? '',
        reasonCode: '',
        reasonTitle: '',
    }
    for (const reason of status?.reasons) {
        const titleId = reasonTitle(status.id, reason.id)
        const codeId = reasonCode(status.id, reason.id)
        schema[titleId] = Yup.string().trim().nullable().required(ValidateMessages.required)
        schema[codeId] = Yup.string().trim().nullable().required(ValidateMessages.required)
        initialValues[titleId] = reason.title
        initialValues[codeId] = reason.code
    }
    return {schema: Yup.object().shape(schema), initialValues}
}

const subscription = {submitting: true, values: true, initialValues: true};

const WarrantyAndReturnStatusCard = ({
                                         status,
                                         updateStatus,
                                         updateReason,
                                         createReason: createReasonProp,
                                         showIsActive,
                                         showIsDefault,
                                         removeStatus,
                                         hideReasons,
                                     }) => {
    const [open, setOpen] = useState(false)
    // these are filled to prevent bug in makeRequired
    const [schema, setSchema] = useState(Yup.object().shape({
        statusTitle: Yup.string().trim().nullable().required(ValidateMessages.required),
        reasonCode: Yup.string().trim().nullable().required(ValidateMessages.required),
        reasonTitle: Yup.string().trim().nullable().required(ValidateMessages.required),
    }))
    const [initialValues, setInitialValues] = useState({})
    const [editing, setEditing] = useState(false)
    const validate = makeValidate(schema)
    const required = makeRequired(schema)

    /**
     * Listens for the changes in status and with each change:
     * sets schema and initialValues of the new status.
     */
    useEffect(() => {
        const {schema, initialValues} = createSchemaAndInitialValues(status)
        setSchema(schema)
        setInitialValues(initialValues)
    }, [status])

    /**
     * Listens for the changes in editing state of the status and with each change:
     * if editing, collapses the reasons section.
     */
    useEffect(() => {
        if (editing) {
            setOpen(false)
        }
    }, [editing])

    /**
     * Creates a new status given the provided title by the user if the new title is not empty or
     * @param {any} values the values of the form
     */
    const saveNewStatusTitle = (values) => {
        if (!values.statusTitle?.length) {
            setEditing(false)
            return;
        }
        if (status.editing) return
        updateStatus({
            ...status,
            title: values.statusTitle
        })
        setEditing(false)
    }

    /**
     * Creates a new reason given the provided title and code by the user if the new title is not empty or
     * @param {any} values the values of the form
     * @param {formApi} form the form
     */
    const createReason = async (values, form) => {
        if (!values.reasonCode?.length || !values.reasonTitle?.length) {
            return;
        }
        if (status.editing) return
        const forApi = {
            title: values.reasonTitle,
            code: values.reasonCode,
            reqStatusId: status.id,
        }
        await createReasonProp(forApi, form)
        form.change('reasonCode', undefined)
        form.resetFieldState('reasonCode')
        form.change('reasonTitle', undefined)
        form.resetFieldState("reasonTitle")
        setEditing(false)
    }

    return (
        <>
            <div className={classnames('part-support-status-card', {'editing': editing})}>
                <Form
                    className={''}
                    onSubmit={() => false}
                    subscription={subscription}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <>
                                <div className={'d-flex justify-content-between align-items-center'}>
                                    <div className={'title-container'}>
                                        <Fade in={editing} className={'d-flex'}>
                                            <div>
                                                <OutlinedInputForm
                                                    name={'statusTitle'}
                                                    placeholder={'title of the status'}
                                                    label={"Status Title"}
                                                    className={'mr-2'}
                                                    required={required.statusTitle}
                                                    value={values.statusTitle ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                                {
                                                    values['statusTitle']?.trim()?.length > 0 &&
                                                    <SaveOrRollback
                                                        column={false}
                                                        loading={status.editing}
                                                        show={true}
                                                        onRollback={() => setEditing(false) || form.change('statusTitle', initialValues.statusTitle)}
                                                        onSave={() => saveNewStatusTitle(values)}
                                                    />
                                                }
                                            </div>
                                        </Fade>
                                        <Fade in={!editing} className={'d-flex align-items-center'}>
                                            <div>
                                                {
                                                    !!removeStatus &&
                                                    <IconButton
                                                        disabled={status.editing || editing}
                                                        className={'mx-1 ml-2'}
                                                        color={'paper.error'}
                                                        onPressed={() => removeStatus(status)}
                                                    >
                                                        <DeleteOutlined className={'icon normal'}/>
                                                    </IconButton>
                                                }
                                                <p className={'title'}>
                                                    {status?.title ?? ''}
                                                </p>
                                                <IconButton
                                                    disabled={status.editing || editing}
                                                    className={'mx-1 ml-2'}
                                                    color={'paper.secondary'}
                                                    onPressed={() => setEditing(true)}
                                                >
                                                    <Edit className={'icon normal'}/>
                                                </IconButton>
                                                {
                                                    showIsActive &&
                                                    <IconButton
                                                        disabled={status.editing || editing}
                                                        className={'mx-1'}
                                                        color={'paper.secondary'}
                                                        onPressed={() => updateStatus({
                                                            ...status,
                                                            isActive: !status.isActive
                                                        })}
                                                    >
                                                        {
                                                            status?.isActive
                                                                ? <Visibility className={'icon normal'}/>
                                                                : <VisibilityOff className={'icon normal'}/>
                                                        }
                                                    </IconButton>
                                                }
                                                {
                                                    showIsDefault &&
                                                    <IconButton
                                                        disabled={status.editing || editing || status.isDefault}
                                                        className={'mx-1'}
                                                        color={'paper.secondary'}
                                                        onPressed={() => updateStatus({
                                                            ...status,
                                                            isDefault: true,
                                                        })}
                                                    >
                                                        {
                                                            status?.isDefault
                                                                ? <StarRounded className={'icon normal'}/>
                                                                : <StarBorderOutlined className={'icon normal'}/>
                                                        }
                                                    </IconButton>
                                                }
                                            </div>
                                        </Fade>
                                    </div>
                                    <Fade in={!editing && !hideReasons}>
                                        <IconButton
                                            disabled={status.editing || editing}
                                            className={'ml-3 toggle-button'}
                                            onPressed={() => setOpen(prevState => !prevState)}
                                        >
                                            <SvgIcon component={ToggleIcon}
                                                     className={classnames(
                                                         'icon normal toggle-icon',
                                                         {'open': open}
                                                     )}
                                            />
                                        </IconButton>
                                    </Fade>
                                </div>
                                <Collapse isOpen={open}>
                                    <div className={'d-flex flex-column'}>
                                        <Col xs={12} className={'d-flex align-items-center p-0 mb-3'}>
                                            <p className={'text-sm font-weight-bold'}>
                                                Reasons
                                            </p>
                                            <div className={'dotted-divider'}/>
                                        </Col>
                                        <div className={'d-flex flex-wrap mb-2 align-items-center mt-4'}>
                                            <Col xs={3} className={'pl-0 pr-1'}>
                                                <OutlinedInputForm
                                                    name={'reasonCode'}
                                                    placeholder={'Code of reason'}
                                                    label={"Code"}
                                                    required={required.reasonCode}
                                                    value={values.reasonCode ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                            <Col className={'pr-0 pl-1'}>
                                                <OutlinedInputForm
                                                    name={'reasonTitle'}
                                                    placeholder={'title of reason'}
                                                    label={"Title"}
                                                    required={required.reasonTitle}
                                                    value={values.reasonTitle ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={'auto'}>
                                                <Button
                                                    className='px-5'
                                                    color={'primary'}
                                                    variant={'contained'}
                                                    onClick={() => createReason(values, form)}
                                                    disabled={
                                                        status?.editing || status?.creatingReason ||
                                                        (values?.reasonTitle?.trim()?.length === 0 || values?.reasonCode?.trim()?.length === 0)
                                                    }
                                                >
                                                    {
                                                        status?.creatingReason
                                                            ? "Adding..."
                                                            : "Add"
                                                    }
                                                </Button>
                                            </Col>
                                        </div>
                                        {
                                            open && !hideReasons &&
                                            <Col xs={12} className={'d-flex flex-wrap w-100 p-0 mt-3 mb-4'}>
                                                {

                                                    status?.reasons?.length < 1
                                                        ? <TryAgain
                                                            text={'There is currently no reason submitted for this status. you' +
                                                                ' can add one using the form above.'}
                                                        />
                                                        : status?.reasons?.map(reason => (
                                                            <ReasonCard
                                                                key={reason.id}
                                                                reason={reason}
                                                                updateReason={updateReason}
                                                                required={required}
                                                                values={values}
                                                                initialValues={initialValues}
                                                                form={form}
                                                            />
                                                        ))
                                                }
                                            </Col>
                                        }
                                    </div>
                                </Collapse>
                            </>
                        );
                    }}
                />
            </div>
        </>
    );
}


const ReasonCard = ({reason, updateReason, required, values, initialValues, form}) => {
    const [editing, setEditing] = useState(false)
    const _reasonCode = reasonCode(reason.reqStatusId, reason.id)
    const _reasonTitle = reasonTitle(reason.reqStatusId, reason.id)

    /**
     * Roll backs the changes made by the user to the reason fields
     */
    const rollbackChanges = () => {
        form.change(_reasonCode, initialValues[_reasonCode])
        form.change(_reasonTitle, initialValues[_reasonTitle])
        setEditing(false);
    }

    /**
     * Invokes the updateReason callback with the updated reason information.
     */
    const saveChanges = () => {
        if (!values[_reasonCode]?.length || !values[_reasonTitle]?.length) {
            setEditing(false)
            return;
        }
        if (deepEqual(values[_reasonCode], initialValues[_reasonCode])
            && deepEqual(values[_reasonTitle], initialValues[_reasonTitle])) {
            setEditing(false)
            return;
        }
        if (reason.editing) return
        updateReason({
            ...reason,
            title: values[_reasonTitle],
            code: values[_reasonCode],
        })
        setEditing(false)
    }

    return (
        <>
            <div className={classnames("reason-card", {'editing': editing})}>
                <div className={'title-container'}>
                    <Fade in={editing} className={'d-flex'}>
                        <div>
                            <Col xs={3} className={'pl-0 pr-1'}>
                                <OutlinedInputForm
                                    name={_reasonCode}
                                    placeholder={'Code of the reason'}
                                    label={"Code"}
                                    className={'mr-2'}
                                    required={required[_reasonCode]}
                                    value={values[_reasonCode] ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                />
                            </Col>
                            <Col className={'pr-0 pl-1'}>
                                <OutlinedInputForm
                                    name={_reasonTitle}
                                    placeholder={'title of reason'}
                                    label={"Title"}
                                    required={required[_reasonTitle]}
                                    value={values[_reasonTitle] ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                />
                            </Col>
                            {
                                (values[_reasonCode]?.trim()?.length > 0 && values[_reasonTitle]?.trim()?.length > 0) &&
                                <SaveOrRollback
                                    column={false}
                                    loading={reason.editing}
                                    show={true}
                                    onRollback={rollbackChanges}
                                    onSave={saveChanges}
                                />
                            }
                        </div>
                    </Fade>
                    <Fade in={!editing} className={'d-flex'}>
                        <div>
                            <Col xs={3} className={'pl-0'}>
                                <p className={'text-sm pl-3'}>
                                    {reason?.code ?? ''}
                                </p>
                            </Col>
                            <Col xs={true}>
                                <p className={'text-sm'}>
                                    {reason?.title ?? ''}
                                </p>
                            </Col>
                            <Col xs={'auto'} className={'d-flex align-items-center justify-content-end'}>
                                <IconButton
                                    disabled={reason.editing || editing}
                                    className={"mx-1 ml-2"}
                                    color={'paper.secondary'}
                                    onPressed={() => setEditing(true)}
                                >
                                    <Edit className={"icon small"}/>
                                </IconButton>
                                <IconButton
                                    disabled={reason.editing || editing}
                                    className={"mx-1"}
                                    color={'paper.secondary'}
                                    onPressed={() => updateReason({
                                        ...reason,
                                        isActive: !reason.isActive
                                    })}
                                >
                                    {
                                        reason?.isActive
                                            ? <Visibility className={'icon small'}/>
                                            : <VisibilityOff className={'icon small'}/>
                                    }
                                </IconButton>
                            </Col>
                        </div>
                    </Fade>
                </div>
            </div>
        </>
    );
}

export default WarrantyAndReturnStatusCard
